import type { BusinessOffer } from '../api/schemas';
import type { CreateOfferInput } from '../api/endpoints/offers';
import { parsePriceInput } from './money';
import { isEveryDay, normalizeDays } from './offer-days';

const MIN_PERCENT = 1;
const MAX_PERCENT = 100;
const MILLISECONDS_PER_MINUTE = 60_000;
const DATE_INPUT_LENGTH = 16;

export function offerDateInput(iso: string): string {
  const date = new Date(iso);
  return new Date(date.getTime() - date.getTimezoneOffset() * MILLISECONDS_PER_MINUTE).toISOString().slice(0, DATE_INPUT_LENGTH);
}

type Draft = { title: string; description: string; discountType: 'percent' | 'fixed'; percentOff: number; amountInput: string; startsAt: string; validUntil: string; validDays: readonly number[] };
/** `clearsDays`: an edited oferta that had days now runs every day, so the patch must send `validDays: null`. */
type Result = { ok: true; input: CreateOfferInput; clearsDays: boolean } | { ok: false; message: string };

export function offerInput(draft: Draft, original: BusinessOffer | null): Result {
  if (!draft.title.trim() || !draft.description.trim()) return { ok: false, message: 'Completa el título y la descripción.' };
  const start = original && draft.startsAt === offerDateInput(original.startsAt) ? original.startsAt : draft.startsAt;
  const end = original && draft.validUntil === offerDateInput(original.validUntil) ? original.validUntil : draft.validUntil;
  if (!start || !end || !Number.isFinite(Date.parse(start)) || !Number.isFinite(Date.parse(end))) return { ok: false, message: 'Revisa las fechas de la oferta.' };
  if (Date.parse(end) <= Date.parse(start)) return { ok: false, message: 'La fecha de vencimiento debe ser después del inicio.' };
  const everyDay = isEveryDay(draft.validDays);
  const clearsDays = everyDay && Boolean(original?.validDays?.length);
  const common = {
    title: draft.title.trim(),
    description: draft.description.trim(),
    startsAt: new Date(start).toISOString(),
    validUntil: new Date(end).toISOString(),
    ...(everyDay ? {} : { validDays: normalizeDays(draft.validDays) }),
  };
  if (draft.discountType === 'percent') {
    if (!Number.isInteger(draft.percentOff) || draft.percentOff < MIN_PERCENT || draft.percentOff > MAX_PERCENT) return { ok: false, message: 'Escribe un porcentaje entre 1 y 100.' };
    return { ok: true, input: { ...common, percentOff: draft.percentOff }, clearsDays };
  }
  const amount = parsePriceInput(draft.amountInput);
  if (!amount.ok) return amount;
  if (amount.cents === 0) return { ok: false, message: 'El descuento debe ser mayor que cero.' };
  return { ok: true, input: { ...common, amountOffCents: amount.cents }, clearsDays };
}
